import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Zap, Activity, ArrowRight } from 'lucide-react';
export function SolutionSection() {
  return <section className="py-24 bg-[#05080f] text-white relative overflow-hidden" data-id="element-89">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-900/10 blur-[120px] rounded-full pointer-events-none" data-id="element-90" />

      <div className="container mx-auto px-4 relative z-10" data-id="element-91">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-20" data-id="element-92">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-6" data-id="element-93">
            THE SOLUTION ISN'T MORE EFFORT. <br data-id="element-94" />
            <span className="text-blue-500" data-id="element-95">IT'S BETTER SEQUENCING.</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8" data-id="element-96">
            Introducing:{' '}
            <strong className="text-white" data-id="element-97">
              Explosive Baseball Power System (College Edition)
            </strong>
          </p>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-98">
            A PDF program built to create repeatable, game-usable power in 30
            days without living in the weight room.
          </p>
        </div>

        {/* The Mechanism */}
        <div className="grid lg:grid-cols-2 gap-16 mb-24" data-id="element-99">
          {/* Stack */}
          <div data-id="element-100">
            <div className="flex items-center gap-3 mb-8" data-id="element-101">
              <Layers className="text-blue-500 w-6 h-6" data-id="element-102" />
              <h3 className="text-2xl font-bold uppercase tracking-wide" data-id="element-103">
                The Explosive Power Stack
              </h3>
            </div>

            <div className="space-y-4" data-id="element-104">
              {[{
              step: '01',
              title: 'Recruitment',
              desc: 'Turn the system on',
              color: 'border-gray-700 bg-gray-900/50'
            }, {
              step: '02',
              title: 'Force',
              desc: 'Build usable strength in hitter positions',
              color: 'border-blue-900/50 bg-blue-900/10'
            }, {
              step: '03',
              title: 'Transfer',
              desc: 'Make it show up in swing speed + contact',
              color: 'border-blue-500 bg-blue-600/10 shadow-[0_0_30px_rgba(59,130,246,0.15)]'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.15
            }} className={`flex items-center p-6 rounded-lg border ${item.color} relative overflow-hidden`} data-id="element-105">
                  <div className="text-4xl font-black text-white/10 mr-6" data-id="element-106">
                    {item.step}
                  </div>
                  <div data-id="element-107">
                    <h4 className="text-xl font-bold text-white" data-id="element-108">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-109">{item.desc}</p>
                  </div>
                  {i === 2 && <div className="absolute right-0 top-0 bottom-0 w-1 bg-blue-500 shadow-[0_0_15px_#3b82f6]" data-id="element-110" />}
                </motion.div>)}
            </div>
          </div>

          {/* Phasing */}
          <div data-id="element-111">
            <div className="flex items-center gap-3 mb-8" data-id="element-112">
              <Activity className="text-amber-500 w-6 h-6" data-id="element-113" />
              <h3 className="text-2xl font-bold uppercase tracking-wide" data-id="element-114">
                Micro-Cycle Phasing
              </h3>
            </div>

            <div className="relative h-full min-h-[300px] bg-[#0f1623] rounded-2xl border border-white/5 p-8 flex flex-col justify-center" data-id="element-115">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 mix-blend-overlay" data-id="element-116"></div>

              <div className="space-y-8 relative z-10" data-id="element-117">
                <div className="flex items-center gap-4" data-id="element-118">
                  <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center border border-amber-500/20 text-amber-500 font-bold text-sm" data-id="element-119">
                    PRIME
                  </div>
                  <div data-id="element-120">
                    <div className="font-bold text-white" data-id="element-121">Sharpen</div>
                    <div className="text-sm text-gray-400" data-id="element-122">
                      Speed + intent, low fatigue
                    </div>
                  </div>
                </div>

                <div className="flex justify-center -my-2 pl-6" data-id="element-123">
                  <ArrowRight className="text-gray-600 rotate-90" data-id="element-124" />
                </div>

                <div className="flex items-center gap-4" data-id="element-125">
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 text-blue-500 font-bold text-sm" data-id="element-126">
                    BUILD
                  </div>
                  <div data-id="element-127">
                    <div className="font-bold text-white" data-id="element-128">Construct</div>
                    <div className="text-sm text-gray-400" data-id="element-129">
                      Strength that transfers
                    </div>
                  </div>
                </div>

                <div className="flex justify-center -my-2 pl-6" data-id="element-130">
                  <ArrowRight className="text-gray-600 rotate-90" data-id="element-131" />
                </div>

                <div className="flex items-center gap-4" data-id="element-132">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center border border-white/20 text-white font-bold text-sm" data-id="element-133">
                    EXPRESS
                  </div>
                  <div data-id="element-134">
                    <div className="font-bold text-white" data-id="element-135">Convert</div>
                    <div className="text-sm text-gray-400" data-id="element-136">
                      Speed that shows up
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center" data-id="element-137">
          <p className="text-xl font-medium text-white" data-id="element-138">
            That's how you become the hitter whose power doesn't require "the
            perfect day."
          </p>
        </div>
      </div>
    </section>;
}